import {
  Component,
  OnInit,
  HostListener,
  EventEmitter,
  Output,
  ViewChild,
} from "@angular/core";
import { BehaviorSubject, Observable, Subject, Subscription } from "rxjs";
import { Store } from "@ngrx/store";
import { RootStoreState } from "src/app/root-store";
import { Router } from "@angular/router";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { takeUntil } from "rxjs/operators";
import * as _ from "underscore";

import { ModalComponent } from "src/app/core/components/modal/modal.component";

// SERVICES
import { StaticDataService } from "src/app/services/static-data.service";

// MODELS
import { Customer } from "src/app/models/Customer.model";
import { PurchaseSize } from "src/app/models/PurchaseSize.model";
import { DrawingLayoutDetail } from "src/app/models/DrawingLayoutDetail.model";
import {
  FloorplannerDesign,
  FloorplannerProject,
} from "src/app/models/Floorplanner.model";

// ACTIONS
import {
  ClearCustomerList,
  GetCustomerList,
} from "src/app/root-store/kwikspace-app-store/actions/customer.action";
import {
  GetBuildings,
  GetLocations,
  GetOrganizations,
  GetProvinces,
  GetValidities,
} from "src/app/root-store/kwikspace-app-store/actions/kwikspace.action";
import {
  ClearTransportAreas,
  ClearTransportDistricts,
  GetTransportAreas,
  GetTransportDistricts,
} from "src/app/root-store/kwikspace-app-store/actions/transport.action";
import { CreatePurchaseQuote } from "src/app/root-store/kwikspace-app-store/actions/quote.action";
import {
  GetPurchaseSize,
  GetDrawingLayoutDetail,
  GetFpProjectCategory,
  GetFpProjectSubCategory,
  GetFpProjectSize,
  GetFpProjectExtras,
  ClearPurchaseLineItem,
} from "src/app/root-store/kwikspace-app-store/actions/purchase.action";
import {
  GetProject,
  GetProjectList,
} from "src/app/root-store/kwikspace-app-store/actions/floorplanner.action";

// SELECTORS
import {
  createPurchaseQuoteLoading,
  createPurchaseQuoteError,
  createPurchaseQuoteSuccess,
  getCreatedPurchaseQuote,
} from "src/app/root-store/kwikspace-app-store/selectors/quote.selector";
import { getCurrentUser } from "src/app/root-store/main-app-store/selectors/auth.selector";
import {
  getCustomerListLoading,
  getCustomerListError,
  getCustomerList,
} from "src/app/root-store/kwikspace-app-store/selectors/customer.selector";
import {
  getBuildings,
  getBuildingsError,
  getBuildingsLoading,
  getLocations,
  getLocationsError,
  getLocationsLoading,
  getOrganizations,
  getOrganizationsError,
  getOrganizationsLoading,
  getProvinces,
  getProvincesError,
  getProvincesLoading,
  getValidities,
  getValiditiesError,
  getValiditiesLoading,
} from "src/app/root-store/kwikspace-app-store/selectors/kwikspace.selector";
import {
  getTransportPriceListLoading,
  getTransportPriceListError,
  getTransportPriceList,
  getTransportAreas,
  getTransportAreasError,
  getTransportAreasLoading,
  getTransportDistricts,
  getTransportDistrictsLoading,
  getTransportDistrictsError,
} from "src/app/root-store/kwikspace-app-store/selectors/transport.selector";
import {
  getPurchaseSizes,
  getPurchaseSizesLoading,
  getPurchaseSizesError,
  getDrawingLayoutDetails,
  getDrawingLayoutDetailsLoading,
  getCreatedLineItemConfigsError,
  getDrawingLayoutDetailsError,
  getFpProjectCategoryListLoading,
  getFpProjectCategoryError,
  getFpProjectSubCategoryListLoading,
  getFpProjectSubCategoryListError,
  getFpProjectSizeListLoading,
  getFpProjectSizeListError,
  getFpProjectExtrasListLoading,
  getFpProjectExtrasListError,
  getFpProjectCategoryList,
  getFpProjectSubCategoryList,
  getFpProjectSizeList,
  getFpProjectExtrasList,
} from "src/app/root-store/kwikspace-app-store/selectors/purchase.selector";
import {
  getDesignError,
  getDesignLoading,
  getProject,
  getProjectError,
  getProjectList,
  getProjectListError,
  getProjectListLoading,
  getProjectLoading,
} from "src/app/root-store/kwikspace-app-store/selectors/floorplanner.selector";
import { FpProjectCategory } from "src/app/models/FpProjectCategory.model";
import { FpProjectSubCategory } from "src/app/models/FpProjectSubCategory.model";
import { FpProjectSize } from "src/app/models/FpProjectSize.model";
import { FpProjectExtra } from "src/app/models/FpProjectExtra.model";

// COMPONENTS

@Component({
  selector: "kwikspace-new-purchase-quote",
  templateUrl: "./new-purchase-quote.component.html",
  styleUrls: ["./new-purchase-quote.component.scss"],
})
export class NewPurchseQuoteComponent implements OnInit {
  private ngUnsubscribe = new Subject();
  private subscribable = new Subscription();

  // @ViewChild("floorplannerViewProjectModal", { static: false })
  // floorplannerViewProjectModal: ModalComponent;

  // @ViewChild("floorplannerViewDesignModal", { static: false })
  // floorplannerViewDesignModal: ModalComponent;

  newPurchaseQuoteLoading$: Observable<any> = this.store.select(
    createPurchaseQuoteLoading
  );
  newPurchaseQuoteError$: Observable<any> = this.store.select(
    createPurchaseQuoteError
  );

  customersLoading$: Observable<any> = this.store.select(
    getCustomerListLoading
  );
  customersError$: Observable<any> = this.store.select(getCustomerListError);

  transportDistrictsLoading$: Observable<any> = this.store.select(
    getTransportDistrictsLoading
  );
  transportDistrictsError$: Observable<any> = this.store.select(
    getTransportDistrictsError
  );

  transportAreasLoading$: Observable<any> = this.store.select(
    getTransportAreasLoading
  );
  transportAreasError$: Observable<any> = this.store.select(
    getTransportAreasError
  );

  provincesLoading$: Observable<any> = this.store.select(getProvincesLoading);
  provincesError$: Observable<any> = this.store.select(getProvincesError);

  purchaseSizesLoading$: Observable<any> = this.store.select(
    getPurchaseSizesLoading
  );
  purchaseSizesError$: Observable<any> = this.store.select(
    getPurchaseSizesError
  );

  drawingLayoutDetailLoading$: Observable<any> = this.store.select(
    getDrawingLayoutDetailsLoading
  );
  drawingLayoutDetailError$: Observable<any> = this.store.select(
    getDrawingLayoutDetailsError
  );

  projectListLoading$: Observable<any> = this.store.select(
    getProjectListLoading
  );
  projectListError$: Observable<any> = this.store.select(getProjectListError);

  projectLoading$: Observable<any> = this.store.select(getProjectLoading);
  projectError$: Observable<any> = this.store.select(getProjectError);

  designLoading$: Observable<any> = this.store.select(getDesignLoading);
  designError$: Observable<any> = this.store.select(getDesignError);

  customers: Customer[] = [];
  quoteLocations: any[] = [];
  buildingTypes: any[] = [];
  organizationalTypes: any[] = [];
  requiredByTypes: any[] = [];
  provinces: any[] = [];
  transportDistricts: any[] = [];
  transportAreas: any[] = [];
  quoteValidities: any[] = [];
  // purchaseSizes: PurchaseSize[] = [];

  // drawingLayoutDetails: DrawingLayoutDetail[] = [];
  // designs: FloorplannerDesign[] = [];
  // projects: FloorplannerProject[] = [];

  selectedBuildingType;
  selectedOrganizationalSector;
  selectedRequireBy;
  selectedProvince;
  selectedDistrict;
  selectedArea;

  quoteForm: FormGroup;
  addressForm: FormGroup;

  customerListLoading = false;
  customerListError = false;

  isAdmin: boolean = false;
  isStaff: boolean = false;
  isCustomer: boolean = false;
  customerSelected: boolean = true;
  locationSelected: boolean = true;
  showLocationDD: boolean = false;
  canSaveQuote: boolean = false;
  showProvinceMsg: boolean = false;
  showDistricts: boolean = false;
  showAreas: boolean = false;
  quoteCreated: boolean = false;
  isClassroom: boolean = false;
  // showDiagramList: boolean = false;
  // designSelected: boolean = false;
  // projectSelected: boolean = false;
  // renderProjectTable: boolean = false;
  // diagramOptionSelected: boolean = false;
  // selectedDiagramOption: string = "";
  // createFloorplanner: boolean = false;
  // existingProjects: boolean = false;
  deliverShipping: boolean = true;

  deliverShipmentChecked: boolean = true;
  collectShipmentChecked: boolean = false;

  user: any = null;
  selectedCategory: string = "";
  customerId: any = null;
  selectedLocation = null;
  createdQuote: any = null;
  // quoteNumber: any = null;
  // selectedSize: any = null;
  // selectedCategory: any = null;
  // selectedDiagram: any = null;
  currentProject: any = null;
  // designIDSelected: number = null;
  // selectedDesignThumbnail: string = null;
  // projectIDSelected: number = null;
  // selectedProjectThumbnail: string = null;
  // selectedProjectThumbnail3d: string = null;
  // selectedFloorId: number = null;
  // selectedProjName: any = null;

  // selectedFpProjectCategory: any = null;
  // selectedFpProjectSubCategory: any = null;
  // selectedFpProjectSize: any = null;
  // selectedFpProjectExtra: any = null;

  searchText: string = "";
  newDiagram: boolean = false;

  // Stepper properties
  activeStep: number = 1;
  stepper = [
    {
      id: 1,
      name: "Quote Details",
      status: "progress",
    },
    {
      id: 2,
      name: "Select Project",
      status: "todo",
    },
    {
      id: 3,
      name: "Create Project",
      status: "todo",
    },
    {
      id: 4,
      name: "Quote Items",
      status: "todo",
    },
  ];

  // Check if mobile and change screen size
  isMobile: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
    window.innerWidth <= 768
  );

  @HostListener("window:resize", ["$event"])
  setIsMobile(event) {
    const isMobile = event.target.innerWidth <= 768;
    this.isMobile.next(isMobile);
  }

  // @Output() onProjectSelect = new EventEmitter<any>();

  constructor(
    private store: Store<RootStoreState.RootState>,
    private router: Router,
    private formBuilder: FormBuilder,
    private staticDataService: StaticDataService
  ) {}

  ngOnInit() {
    this.requiredByTypes = this.staticDataService.getRequiredBy();

    // Get current user details
    this.store
      .select(getCurrentUser)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((res) => {
        if (res) {
          this.user = res;
          this.isStaff = res.isStaff;
          this.isCustomer = !res.isStaff && res.customer != null ? true : false;
          this.isAdmin = res.isSuperuser;

          if (this.isStaff && !this.isCustomer) {
            this.customerSelected = false;
            if (this.user.salesRep && this.user.salesRep.locations.length > 1) {
              this.locationSelected = false;
              this.showLocationDD = true;
              this.quoteLocations = this.user.salesRep.locations;
            }
            this.getCustomers();
          } else {
            this.locationSelected = false;
            this.getSalesLocations();
          }
        }
      });

    this.store
      .select(getBuildings)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((buildings: any) => {
        if (buildings) {
          this.buildingTypes = buildings;
        }
      });

    this.store
      .select(getOrganizations)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((organizations: any) => {
        if (organizations) {
          this.organizationalTypes = organizations;
        }
      });

    this.store
      .select(getValidities)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((validities: any) => {
        if (validities) {
          this.quoteValidities = validities;
        }
      });

    this.store
      .select(getProvinces)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((provinces: any) => {
        if (provinces && provinces.results) {
          this.provinces = provinces.results;
        }
      });

    // Get Floorplanner projects and designs
    // this.store
    // .select(getProjectList)
    // .pipe(takeUntil(this.ngUnsubscribe))
    // .subscribe((res: any) => {
    //   if (res && res.results) {
    //     this.projects = res.results.length > 0 ? res.results : [];
    //   }
    // });

    this.quoteForm = this.formBuilder.group({
      customer: [{ value: null, disabled: true }, [Validators.required]],
      searchCustomer: [null, []],
      building_type: [null, [Validators.required]],
      organization_sector: [null, [Validators.required]],
      your_reference: [null, []],
      customer_account_number: [null, []],
      site_allocation: [null, []],
      quote_validity: [null, []],
      quote_location: [null, []],
      notes: [null, []],
      line_items: [null, []],
      purchase_line_items: [null, []],
      required_by: [null, [Validators.required]],
    });

    this.addressForm = this.formBuilder.group({
      shipping_address1: [null, [Validators.required]],
      shipping_address2: [null, []],
      shipping_suburb: [null, [Validators.required]],
      shipping_province: [null, [Validators.required]],
      shipping_district: [null, []],
      shipping_area: [null, []],
      shipping_postal: [null, []],
    });

    this.store.dispatch(new GetProvinces());
    this.store.dispatch(new GetBuildings());
    this.store.dispatch(new GetOrganizations());
    this.store.dispatch(new GetValidities());
    // this.store.dispatch(new GetProjectList());
    this.store.dispatch(new ClearPurchaseLineItem());
  }

  getCustomers() {
    this.store
      .select(getCustomerList)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((customers: any) => {
        if (customers?.results) {
          this.customers = customers.results;
        }
      });

    this.store.dispatch(new GetCustomerList({}));
  }

  searchCustomers() {
    if (this.searchText !== "") {
      this.store.dispatch(new ClearCustomerList());

      this.store.dispatch(
        new GetCustomerList({
          search: this.searchText,
        })
      );
    }
  }

  setSelectedCustomer(customer) {
    this.customerId = customer.id;
    this.customerSelected = true;
    this.quoteForm
      .get("customer")
      .setValue(`${customer.firstName} ${customer.lastName}`);
  }

  getSalesLocations() {
    this.store
      .select(getLocations)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((locations: any) => {
        if (locations?.results) {
          this.showLocationDD = true;
          this.quoteLocations = locations.results;
        }
      });

    this.store.dispatch(new GetLocations());
  }

  setSelectedQuoteLocation() {
    this.selectedLocation = this.quoteForm.value.quote_location;
    this.locationSelected = this.selectedLocation != null ? true : false;
  }

  setBuildingType() {
    this.selectedBuildingType = this.quoteForm.value.building_type;
  }

  setOrganizationalSector() {
    this.selectedOrganizationalSector =
      this.quoteForm.value.organization_sector;
  }

  setRequiredBy() {
    this.selectedRequireBy = this.quoteForm.value.required_by;
  }

  setDeliveryOption(val) {
    this.deliverShipmentChecked = val ? true : false;
    this.collectShipmentChecked = this.deliverShipmentChecked ? false : true;

    this.deliverShipping = this.deliverShipmentChecked;

    this.canSaveQuote = this.collectShipmentChecked || this.deliverShipmentChecked ? true : false;
  }

  setProvince() {
    this.store.dispatch(new ClearTransportDistricts());
    this.store.dispatch(new ClearTransportAreas());

    this.selectedProvince = this.addressForm.value.shipping_province;

    const _id = this.provinces.findIndex(
      (obj) => obj.id == this.selectedProvince
    );

    const province = this.provinces[_id].name;

    if (province != "Other") {
      this.canSaveQuote = false;
      this.showProvinceMsg = false;
      this.showDistricts = true;
      this.getTransportDistricts();
    } else {
      this.selectedProvince = null;
      this.canSaveQuote = true;
      this.showDistricts = false;
      this.showProvinceMsg = true;
      this.showAreas = false;
    }
  }

  getTransportDistricts() {
    const payload = {
      filter: {
        filterCategory: "province",
        filter: this.selectedProvince,
      },
    };

    this.store.dispatch(new GetTransportDistricts(payload));

    this.store
      .select(getTransportDistricts)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((districts: any) => {
        if (districts) {
          this.transportDistricts = districts;
        }
      });
  }

  setDistrict() {
    this.selectedDistrict = this.addressForm.value.shipping_district;
    this.showAreas = true;
    this.getTransportAreas();
  }

  getTransportAreas() {
    const payload = {
      filter: {
        filterCategory: "district",
        filter: this.selectedDistrict,
      },
    };

    this.store.dispatch(new GetTransportAreas(payload));

    this.store
      .select(getTransportAreas)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((areas: any) => {
        if (areas) {
          this.transportAreas = areas;
        }
      });
  }

  setArea() {
    this.selectedArea = this.addressForm.value.shipping_area;
    this.canSaveQuote = true;
  }

  saveQuote() {
    let quoteLocation = this.getLocation();

    const payload: any = {
      quote_status: "new",
      quote_type: "purchase",
      quote_validity: this.quoteValidities ? this.quoteValidities[0].id : null,
      building: this.selectedBuildingType,
      organization: this.selectedOrganizationalSector,
      your_reference: "Rev00",
      customer_account_number: this.quoteForm.value.customer_account_number
        ? this.quoteForm.value.customer_account_number
        : "",
      site_allocation: this.quoteForm.value.site_allocation
        ? this.quoteForm.value.site_allocation
        : "",
      notes: this.quoteForm.value.notes ? this.quoteForm.value.notes : "",
      line_items: [],
      purchase_line_items: [],
      quote_location: quoteLocation,
      required_by: this.quoteForm.value.required_by,
      shipping: this.deliverShipping ? true : false,
      shipping_address1: this.deliverShipping ? this.addressForm.value.shipping_address1 : null,
      shipping_address2: this.deliverShipping ? this.addressForm.value.shipping_address2 : null,
      shipping_postal: this.deliverShipping ? this.addressForm.value.shipping_postal : null,
      shipping_province: this.deliverShipping ? this.selectedProvince : null,
      shipping_area: this.selectedArea ? this.selectedArea : null,
      shipping_suburb: this.deliverShipping ? this.addressForm.value.shipping_suburb : null,
    };

    if (this.isStaff) {
      if (this.user.salesRep && !this.isAdmin) {
        payload.quote_owner = this.user.salesRep.id;
      }
      payload.customer = this.customerId;
    }

    if (this.isCustomer) {
      payload.customer = this.user.customer.id;
    }

    this.store
      .select(createPurchaseQuoteSuccess)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((quote) => {
        if (quote) {
          this.activeStep = 2;
          this.stepper[0].status = "complete";
          this.stepper[1].status = "progress";
        }
      });

    this.store
      .select(getCreatedPurchaseQuote)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((quote) => {
        if (quote) {
          this.createdQuote = quote;
          this.quoteCreated = true;
        }
      });

    this.store.dispatch(new CreatePurchaseQuote(payload));
  }

  getLocation() {
    let location;

    if (this.isStaff && !this.isCustomer) {
      location = this.selectedLocation
        ? this.selectedLocation
        : this.user.salesRep.locations[0].id;
    } else if (this.isCustomer) {
      location = this.selectedLocation;
    }

    return location;
  }

  // setFpProjectCategory() {
  //   this.selectedFpProjectCategory = this.purchaseForm.value.project_category;
  //   this.getProjectList();
  // }

  // setFpProjectSubCategory() {
  //   this.selectedFpProjectSubCategory =
  //     this.purchaseForm.value.project_sub_category;
  //   this.getProjectList();
  // }

  // setFpProjectSize() {
  //   this.selectedFpProjectSize = this.purchaseForm.value.project_size;
  //   this.getProjectList();
  // }

  // getProjectList() {
  //   if (
  //     this.selectedFpProjectCategory != null &&
  //     this.selectedFpProjectSubCategory != null &&
  //     this.selectedFpProjectSize != null
  //   ) {
  //     // get projects with selections
  //   }
  // }

  // setFpProjectExtra() {
  //   this.selectedFpProjectExtra = this.purchaseForm.value.project_extra;
  //   this.filterByExtras();
  // }

  // filterByExtras() {
  //   // filter by variation
  //   // needs to be in a new array
  // }

  // setCategory() {
  //   this.selectedCategory = this.purchaseForm.value.purchase_size;
  // }

  // setSize() {
  //   this.selectedSize = this.purchaseForm.value.purchase_size;
  // }

  // Get all the drawing layouts
  // getDrawingLayoutDetails() {
  //   this.showDiagramList = true;
  //   if (this.selectedSize != null) {
  //     this.store
  //       .select(getDrawingLayoutDetails)
  //       .pipe(takeUntil(this.ngUnsubscribe))
  //       .subscribe((diagrams) => {
  //         if (diagrams) {
  //           this.drawingLayoutDetails = diagrams;
  //         }
  //       });
  //     this.store.dispatch(new GetDrawingLayoutDetail());
  //   } else {
  //     this.showDiagramList = false;
  //   }
  // }

  // // Set the Diagram list options
  // setDiagramList() {
  //   this.existingProjects = true;
  //   // this.selectedDiagram = this.purchaseForm.value.drawing_layout_detail;
  //   this.getProject();
  // }

  // // Get project list
  // getProject() {
  //   this.renderProjectTable = true;
  //   if (
  //     this.selectedBuildingType != null &&
  //     this.selectedSize != null &&
  //     this.selectedDiagram != null
  //   ) {
  //     this.store.dispatch(new GetProjectList());
  //     this.store
  //       .select(getProject)
  //       .pipe(takeUntil(this.ngUnsubscribe))
  //       .subscribe((res) => {
  //         if (res) {
  //           this.currentProject = res;
  //           if (res.floors) {
  //             this.selectedFloorId = res.floors[0].id;
  //             this.designs = res.floors[0].designs;
  //           }
  //         }
  //       });
  //   } else {
  //     this.renderProjectTable = false;
  //   }
  // }

  // // View selected project
  // viewSelectedProject(project) {
  //   this.selectedProjectThumbnail = project.thumbnail;
  //   this.selectedProjectThumbnail3d = project.thumbnail3d;

  //   // this.floorplannerViewProjectModal.open();
  // }

  // // Set selected project
  // setSelectedProject(project) {
  //   this.projectIDSelected = project.floorplannerProjectId;
  //   this.projectSelected = true;

  //   this.getSelectedProject();
  // }

  // getSelectedProject() {
  //   this.store.dispatch(new GetProject(this.projectIDSelected));

  //   this.store
  //     .select(getProject)
  //     .pipe(takeUntil(this.ngUnsubscribe))
  //     .subscribe((res) => {
  //       if (res) {
  //         this.currentProject = res;
  //         if (res.floors) {
  //           this.selectedFloorId = res.floors[0].id;
  //           this.designs = res.floors[0].designs;

  //           this.selectedProjName = res.name;
  //           console.log("Selected Project ", this.currentProject);
  //         }
  //       }
  //     });
  // }

  // // View selected design
  // viewSelectedDesign(design) {
  //   let selectedDesign = this.designs.filter(
  //     (designItem) => designItem.id === design.id
  //   );
  //   this.selectedDesignThumbnail = selectedDesign[0].thumbUrl;
  //   // console.log("Designs ", selectedDesign)

  //   // this.floorplannerViewDesignModal.open();
  // }

  // // Set selected design
  // setSelectedDesign(design) {
  //   this.designIDSelected = design.id;
  //   this.designSelected = true;

  //   let selectedOptions = {
  //     project: this.currentProject,
  //     projectId: this.projectIDSelected,
  //     floorId: this.selectedFloorId,
  //     design,
  //     designId: this.designIDSelected,
  //   };

  //   this.currentProject = design;
  //   this.activeStep = 3;
  //   this.stepper[1].status = "complete";
  //   this.stepper[2].status = "progress";

  //   this.onProjectSelect.emit(selectedOptions);
  // }

  // templateSelection(template) {
  //   this.diagramOptionSelected = true;
  //   this.selectedDiagramOption = template;
  //   this.renderProjectTable = false;
  //   this.createFloorplanner = true;
  //   this.existingProjects = false;
  // }

  // scroll() {
  //   this.renderProjectTable = true;
  //   this.createFloorplanner = false;
  //   this.existingProjects = true;
  //   this.diagramOptionSelected = false;
  //   this.projectSelected = false;
  //   this.getProject();
  // }

  projectSelected(project) {
    this.currentProject = project;

    this.activeStep = 4;
    this.stepper[1].status = "complete";
    this.stepper[3].status = "progress";
  }

  createNewDiagram(event) {
    this.newDiagram = true;
    this.selectedCategory = event.category;

    this.activeStep = 3;
    this.stepper[1].status = "complete";
    this.stepper[2].status = "progress";

    console.log("Data: ", this.newDiagram)
  }

  ngOnDestroy() {
    this.subscribable.unsubscribe();
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }
}
